import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, useSlots, watch } from 'vue';
import BaseInput from "@/components-UI/BaseInput.vue";
import BasePopup from "@/components-UI/BasePopup.vue";
import ArrowDownSvg from '@/assets/icons/arrow-down.svg';
import CheckDarkSvg from '@/assets/icons/check-dark.svg';
import BaseText from "@/components-UI/BaseText.vue";
import BaseOptions from "@/components-UI/BaseOptions.vue";
import BaseCheckbox from "@/components-UI/BaseCheckbox.vue";

type UnknownItem = { [key: string]: unknown };


export default /*#__PURE__*/_defineComponent({
  __name: 'BaseSelect',
  props: {
    value: { type: [Array, String, Number, Boolean] },
    items: { default: () => [] },
    label: null,
    placeholder: null,
    disabled: { type: Boolean },
    clearable: { type: Boolean },
    clearableType: null,
    view: null,
    size: { default: 'large' },
    position: { default: 'bottom' },
    itemValue: null,
    itemLabel: null,
    template: null,
    loading: { type: Boolean },
    filterable: { type: Boolean },
    emptyText: { default: 'Ничего не найдено' },
    multiple: { type: Boolean },
    isChip: { type: Boolean },
    maxChips: null,
    isDeletableChips: { type: Boolean, default: true },
    chipMaxWidth: null,
    chipsTooltipWidth: null,
    smallSize: { type: Boolean },
    teleportToBody: { type: Boolean, default: true },
    minWidth: { default: 100 },
    isFitContentOptions: { type: Boolean },
    optionsWidthFactor: null,
    isTeleportChipTooltip: { type: Boolean, default: true },
    templateChips: null,
    showValueTitle: { type: Boolean },
    chipKey: null
  },
  emits: [
    'update:value',
    'input',
    'focus',
    'blur',
    'clear',
    'change',
    'select',
],
  setup(__props: any, { emit: emits }) {

const props = __props as { value?: Array<UnknownItem | string> | string | number | boolean, items: UnknownItem[], label?: string, placeholder?: string, disabled?: boolean, clearable?: boolean, clearableType?: 'inner' | 'out', view?: 'border-bottom' | 'outlined', size: 'small' | 'medium' | 'large', position: string, itemValue: string, itemLabel: string, template?: (item: UnknownItem) => string, loading?: boolean, filterable?: boolean, emptyText: string, multiple?: boolean, isChip?: boolean, maxChips?: number, isDeletableChips: boolean, chipMaxWidth?: string, chipsTooltipWidth?: string, smallSize?: boolean, teleportToBody: boolean, minWidth: number, isFitContentOptions?: boolean, optionsWidthFactor?: number, isTeleportChipTooltip: boolean, templateChips?: (item: { [key: string]: unknown }) => string, showValueTitle?: boolean, chipKey?: string }



const slots = useSlots();

const inputValue = ref<string>('');
const chipsValue = ref<UnknownItem[]>([]);
const isMenuOpen = ref<boolean>(false);

const getItems = computed<UnknownItem[]>(() => {
    const list = props.items ?? [];
    if (props.filterable) {
        const query = String(inputValue.value ?? '').toLowerCase();
        return list.filter((el: UnknownItem) => {
            const raw = props.template ? props.template(el) : el[props.itemLabel];
            const text = String(raw ?? '').toLowerCase();
            return text.includes(query);
        });
    }

    return list;
});

const resolveSelectedEntry = (e: UnknownItem | string | number): unknown => {
    if (e !== null && typeof e === 'object' && !Array.isArray(e)) {
        const o = e as UnknownItem;
        if (props.chipKey) {
            const byChip = o[props.chipKey];
            if (byChip !== undefined) {
                return byChip;
            }
        }
        return o[props.itemValue];
    }
    return e;
};

const getFindChips = computed<Array<UnknownItem | string>>(() => {
    const values = props.value;
    if (!Array.isArray(values)) {
        return [];
    }
    return getItems.value.filter((el: UnknownItem) =>
        values.some((e: UnknownItem | string | number) =>
            el[props.itemValue] === resolveSelectedEntry(e),
        ),
    );
});

const getFindItem = computed<UnknownItem | undefined>(() => {
    return getItems.value.find((el: UnknownItem) => el[props.itemValue] === props.value);
});

const isSelectRow = (item: UnknownItem): boolean => {
    return !props.multiple && (
        (getFindItem.value?.[props.itemValue] === item[props.itemValue]) ||
        (props.value === item[props.itemValue])
    );
};

const isCheckedRow = (item: UnknownItem): boolean => {
    if (props.multiple && Array.isArray(props.value)) {
        return props.value.some((e: UnknownItem | string | number) =>
            String(resolveSelectedEntry(e)) === String(item[props.itemValue]),
        );
    }

    return false;
};

const handleSelect = ({ item, index }: { item: UnknownItem, index: number }): void => {
    if (props.isChip) {
        inputValue.value = '';
        isMenuOpen.value = false;
        emits('update:value', item[props.itemValue]);
        emits('select', { item, index });
        return;
    }

    if (props.multiple) {
        const chipIndex = chipsValue.value.findIndex((chip: UnknownItem | string) =>
            String(chip[props.itemValue]) === String(item[props.itemValue]),
        );

        if (chipIndex > -1) {
            chipsValue.value.splice(chipIndex, 1);
            item.checked = false;
        } else {
            chipsValue.value.push({ ...item, checked: true });
        }

        inputValue.value = '';
        emits('update:value', chipsValue.value.map((el: UnknownItem & { checked: boolean }) => el[props.itemValue]));
        emits('select', { item, index });
        return;
    }

    isMenuOpen.value = false;
    emits('update:value', item[props.itemValue]);
    emits('select', { item, index });
};

const handleDeleteChip = (): void => {
    emits('update:value', getFindChips.value.map(el => el[props.itemValue]));
};

const handleClear = (): void => {
    inputValue.value = '';
    chipsValue.value = [];

    if (props.isChip) {
        emits('update:value', '');
        return;
    }

    if (props.multiple) {
        emits('update:value', []);
        return;
    }

    emits('clear');
    emits('update:value', '');
};

watch(() => props.value, () => {
    if (props.isChip && !Array.isArray(props.value)) {
        const found = getFindItem.value;
        chipsValue.value = found ? [found] : [];
        return;
    }

    if (props.multiple && Array.isArray(props.value)) {
        chipsValue.value = getFindChips.value as UnknownItem[];
        return;
    }

    if (!props.multiple || !props.isChip) {
        const found = getFindItem.value;
        if (!found) {
            inputValue.value = '';
            return;
        }
        const raw = props.template ? props.template(found) : found[props.itemLabel];
        inputValue.value = String(raw ?? '');
    }
}, { deep: true, immediate: true });

return { __sfc: true,props, emits, slots, inputValue, chipsValue, isMenuOpen, getItems, resolveSelectedEntry, getFindChips, getFindItem, isSelectRow, isCheckedRow, handleSelect, handleDeleteChip, handleClear, BaseInput, BasePopup, ArrowDownSvg, CheckDarkSvg, BaseText, BaseOptions, BaseCheckbox }
}

})